/**
 * DynamicIntakeForm - Schema-driven intake form
 *
 * Replaces the hardcoded ExtractedFieldsComponent with a dynamic
 * form that renders based on the intake form schema.
 */

import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  VirtualizedFormSelect,
  Alert,
} from '@roger/ui-components';
import { Loader2, AlertCircle, Check } from 'lucide-react';
import MarkCompletedButton from './MarkCompletedButton';
import { DuplicatePatientBanner } from './DuplicatePatientBanner';
import { type IntakeResponse } from '../../services/api';
import { useIntake } from '../../hooks/useIntake';
import { IntakeSectionRenderer } from './IntakeSectionRenderer';
import { runValidation } from './validators';
import { applyFormatter } from './formatters';
import {
  useIntakeFormSchema,
  getSortedSections,
  getRequiredFields,
  type IntakeFieldDefinition,
} from '../../config/intakeFormSchema';
import { useStore } from '../../store';
import { IntakeTabValidationKey } from '../../store/slices/intakeSlice';
import { FeatureFlag } from '../../constants/featureFlags';
import { usePatientMatching } from '../../hooks/useCreatePatientForm/usePatientMatching';
import { PatientMatchModal } from '../../pages/CreatePatientForm/PatientMatchModal';
import {
  excludePatientIdForRogerDuplicateCheck,
  getLatestActiveEpisode,
  toEpisodeDateOnly,
} from '../../pages/CreatePatientForm/patientMatchUtils';
import { formatDateToISO } from '../../utils/dateUtils';
import { useIntakeFieldSave } from '../../hooks/useIntakeFieldSave';

// =============================================================================
// Types
// =============================================================================

export interface DynamicIntakeFormProps {
  intake: IntakeResponse;
  intakeId: string;
  formData: Partial<IntakeResponse>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<IntakeResponse>>>;
  onConfirm: () => void | Promise<void>;
  onIntakeUpdate: () => void;
  agencyInsuranceOptions: Array<{ value: string; label: string }>;
  agencyInsurancesLoading: boolean;
  agencyInsurancesError: string | null;
  agencyClinicOptions: Array<{ value: string; label: string }>;
  agencyClinicsLoading: boolean;
  agencyClinicsError: string | null;
  visibleTabCount: number;
  documentNameDict: Record<string, string>;
  /** RogerDoc clinic key for Kinnser duplicate check (from selected agency clinic). */
  clinicKeyForMatch?: string | null;
  serverFieldErrors?: Record<string, string>;
  onServerFieldErrorCleared?: (fieldId: string) => void;
}

const EMPTY_SERVER_FIELD_ERRORS: Record<string, string> = {};

// =============================================================================
// Component
// =============================================================================

export const DynamicIntakeForm: React.FC<DynamicIntakeFormProps> = ({
  intake,
  intakeId,
  formData,
  setFormData,
  onConfirm,
  onIntakeUpdate: _onIntakeUpdate,
  agencyInsuranceOptions,
  agencyInsurancesLoading,
  agencyInsurancesError,
  agencyClinicOptions,
  agencyClinicsLoading,
  agencyClinicsError,
  visibleTabCount,
  documentNameDict,
  clinicKeyForMatch,
  serverFieldErrors = EMPTY_SERVER_FIELD_ERRORS,
  onServerFieldErrorCleared,
}) => {
  // =========================================================================
  // Schema Loading
  // =========================================================================

  const {
    schema,
    isLoading: schemaLoading,
    error: schemaError,
  } = useIntakeFormSchema();

  // Get the intake update function
  const { updateIntakeRecord } = useIntake();

  const hasFeatureFlag = useStore(state => state.hasFeatureFlag);
  const user = useStore(state => state.user);
  const showAIExplanations = hasFeatureFlag(
    FeatureFlag.INTAKE_SEE_AI_EXPLANATION
  );
  const allowSelectExistingPatient = hasFeatureFlag(
    FeatureFlag.INTAKE_EXISTING_PATIENT_WORKFLOW
  );
  const kinnserIntakeEnabled = user?.agency?.is_kinnser_enabled === true;
  const rogerIntakeEnabled = user?.agency?.is_rogeremr_enabled === true;
  // Roger existing-patient flow is a sub-flag of the generic existing-patient
  // workflow: it only takes effect when `allowSelectExistingPatient` is also on.
  const rogerExistingPatientEnabled =
    allowSelectExistingPatient &&
    hasFeatureFlag(FeatureFlag.INTAKE_EXISTING_PATIENT_WORKFLOW_ROGER);

  // =========================================================================
  // State
  // =========================================================================

  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | null>
  >({});

  // Ref for the clinic select to manually set value
  const clinicSelectRef = React.useRef<HTMLInputElement>(null);

  // Track which intake has been formatted on load
  const formattedIntakeIdRef = React.useRef<string | null>(null);

  // Track fields the user has edited so resolveFieldValue doesn't
  // fall back to stale JSONB values when a field is intentionally cleared.
  const dirtyFieldsRef = useRef<Set<string>>(new Set());
  const prevIntakeIdRef = useRef<string | null>(null);
  if (prevIntakeIdRef.current !== intakeId) {
    prevIntakeIdRef.current = intakeId;
    dirtyFieldsRef.current = new Set();
  }

  // Save utilities — status indicator + debounce + immediate save
  const { saveStatus, saveField, debouncedSave, cancelDebounce } =
    useIntakeFieldSave({
      intakeId,
      updateIntakeRecord,
    });

  // =========================================================================
  // Effects
  // =========================================================================

  // Set clinic select value when formData changes or options load
  useEffect(() => {
    if (clinicSelectRef.current && formData.agency_clinic_id) {
      clinicSelectRef.current.value = formData.agency_clinic_id;
      // Trigger change event to ensure VirtualizedFormSelect syncs
      clinicSelectRef.current.dispatchEvent(
        new Event('change', { bubbles: true })
      );
    }
  }, [formData.agency_clinic_id, agencyClinicOptions]);

  // =========================================================================
  // Computed values
  // =========================================================================

  // Get sorted sections for rendering
  const sortedSections = useMemo(() => {
    if (!schema) return [];
    return getSortedSections(schema);
  }, [schema]);

  // Get missing required fields from schema
  const missingFields = useMemo(() => {
    if (!schema) return [];
    const requiredFields = getRequiredFields(schema);
    return requiredFields.filter(field => {
      const value = formData[field.field_id as keyof IntakeResponse];
      return (
        !value ||
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && value.length === 0)
      );
    });
  }, [formData, schema]);

  const missingFieldsCount = missingFields.length;

  // =========================================================================
  // Effects
  // =========================================================================

  // Validate fields when formData changes
  useEffect(() => {
    const errors: Record<string, string | null> = {};

    // Validate all fields that have validators defined
    for (const [, section] of sortedSections) {
      for (const field of section.content) {
        if (field.validator_fn) {
          const value = formData[field.field_id as keyof IntakeResponse];
          errors[field.field_id] = runValidation(
            field.validator_fn,
            value as string
          );
        }
      }
    }

    setValidationErrors(errors);
  }, [formData, sortedSections]);

  const displayedValidationErrors = useMemo(() => {
    const merged = { ...validationErrors };
    Object.entries(serverFieldErrors).forEach(([fieldId, message]) => {
      if (message) merged[fieldId] = message;
    });
    return merged;
  }, [serverFieldErrors, validationErrors]);

  // Report extraction tab validation so Push to EMR can block onboarding when any tab has errors
  const setIntakeTabValid = useStore(state => state.setIntakeTabValid);
  const hasExtractionErrors =
    missingFieldsCount > 0 ||
    Object.values(displayedValidationErrors).some(err => err != null);
  useEffect(() => {
    setIntakeTabValid(IntakeTabValidationKey.Extraction, !hasExtractionErrors);
  }, [hasExtractionErrors, setIntakeTabValid]);

  // Apply formatters to loaded data (runs once per intake)
  useEffect(() => {
    // Skip if already formatted this intake
    if (formattedIntakeIdRef.current === intakeId) return;
    // Skip if schema or data not ready
    if (!schema || !formData || Object.keys(formData).length === 0) return;

    const formatted: Partial<IntakeResponse> = { ...formData };
    const changedFields: Record<string, unknown> = {};

    Object.values(schema).forEach(section => {
      section.content.forEach(field => {
        if (field.formatter_fn) {
          const value = formData[field.field_id as keyof IntakeResponse];
          if (typeof value === 'string' && value) {
            const formattedValue = applyFormatter(field.formatter_fn, value);
            if (formattedValue !== value) {
              (formatted as Record<string, unknown>)[field.field_id] =
                formattedValue;
              changedFields[field.field_id] = formattedValue;
            }
          }
        }
      });
    });

    // Mark as formatted for this intake (before setState to avoid re-runs)
    formattedIntakeIdRef.current = intakeId;

    if (Object.keys(changedFields).length > 0) {
      // Defer to next tick to avoid React batching issues
      setTimeout(() => {
        setFormData(formatted);
        // Save formatted fields to backend
        updateIntakeRecord(intakeId, changedFields);
      }, 0);
    }
  }, [schema, formData, intakeId, setFormData, updateIntakeRecord]);

  // =========================================================================
  // Patient duplicate check (extraction tab)
  // =========================================================================

  const agencyClinicIdForMatch =
    (formData.agency_clinic_id ?? '').toString().trim() || null;

  const patientMatching = usePatientMatching({
    clinicKey: clinicKeyForMatch ?? undefined,
    agencyClinicId: agencyClinicIdForMatch,
    excludePatientId: excludePatientIdForRogerDuplicateCheck(intake),
    kinnserIntakeEnabled,
    rogerIntakeEnabled,
    rogerExistingPatientEnabled,
  });
  const {
    patientMatches,
    isMatchingPatients,
    patientMatchModalOpen,
    setPatientMatchModalOpen,
    checkPatientMatch,
    duplicateCheckReady,
  } = patientMatching;
  const initialMatchDoneRef = useRef(false);
  const lastIntakeIdRef = useRef<string | null>(null);
  if (lastIntakeIdRef.current !== intakeId) {
    lastIntakeIdRef.current = intakeId ?? null;
    initialMatchDoneRef.current = false;
  }

  // Run duplicate check with current form data (used by effect).
  const runDuplicateCheckWithFormData = useCallback(
    (options?: { openModalOnMatches?: boolean }) => {
      const firstName = (formData.patient_first_name ?? '').toString().trim();
      const lastName = (formData.patient_last_name ?? '').toString().trim();
      if (!firstName || !lastName) return;
      const dob = formData.patient_dob;
      const dobStr =
        dob == null
          ? null
          : typeof dob === 'string'
            ? dob
            : formatDateToISO(dob as Date);
      checkPatientMatch(
        {
          firstName,
          middleName: (formData.patient_middle_name as string) ?? null,
          lastName,
          mrn:
            (formData as { kinnser_mrn?: string | null }).kinnser_mrn ?? null,
          dob: dobStr,
          ssn: (formData.patient_ssn as string) ?? null,
          phone: (formData.patient_phone as string) ?? null,
          address: (formData.patient_street_address as string) ?? null,
          zipCode: (formData.patient_zip_code as string) ?? null,
          gender: (formData.patient_gender as string) ?? null,
          email: (formData.patient_email as string) ?? null,
          city: (formData.patient_city as string) ?? null,
          state: (formData.patient_state as string) ?? null,
        },
        options
      );
    },
    [
      formData.patient_first_name,
      formData.patient_last_name,
      formData.patient_middle_name,
      formData.patient_dob,
      formData.patient_ssn,
      formData.patient_street_address,
      formData.patient_zip_code,
      formData.patient_gender,
      formData.patient_email,
      formData.patient_city,
      formData.patient_state,
      (formData as { kinnser_mrn?: string | null }).kinnser_mrn,
      formData.patient_phone,
      checkPatientMatch,
    ]
  );

  useEffect(() => {
    if (!duplicateCheckReady) return;
    const firstName = (formData.patient_first_name ?? '').toString().trim();
    const lastName = (formData.patient_last_name ?? '').toString().trim();
    if (!firstName || !lastName) return;

    const openModalOnMatches = false;

    if (!initialMatchDoneRef.current) {
      initialMatchDoneRef.current = true;
      runDuplicateCheckWithFormData({ openModalOnMatches });
      return;
    }

    const t = setTimeout(
      () => runDuplicateCheckWithFormData({ openModalOnMatches }),
      800
    );
    return () => clearTimeout(t);
  }, [
    duplicateCheckReady,
    intake,
    formData.patient_first_name,
    formData.patient_last_name,
    runDuplicateCheckWithFormData,
    allowSelectExistingPatient,
  ]);

  // Fields nulled whenever the intake flips back to "new patient" — clears any
  // existing-patient match identifiers AND scheduling state (ROC-only visit types,
  // creation types, ROC dates) that only make sense for an existing patient.
  const markNewPatientPayload = useMemo(
    () =>
      ({
        is_new_patient: true,
        patient_id: null,
        kinnser_matched_patient_key: null,
        kinnser_matched_discharge_date: null,
        kinnser_matched_start_of_care: null,
        kinnser_matched_episode_start_date: null,
        kinnser_matched_episode_end_date: null,
        kinnser_matched_episode_key: null,
        patient_match_source: null,
        kinnser_visit_type: null,
        kinnser_creation_type: null,
        roc_date: null,
      }) as Partial<IntakeResponse>,
    []
  );
  useEffect(() => {
    if (
      !allowSelectExistingPatient &&
      patientMatches != null &&
      patientMatches.length > 0
    ) {
      const isNewPatient = (intake as { is_new_patient?: boolean | null })
        .is_new_patient;
      // Only auto-mark when not already decided (null/undefined); skip if already new or existing
      if (isNewPatient != null) return;
      void updateIntakeRecord(intakeId, markNewPatientPayload);
    }
  }, [
    allowSelectExistingPatient,
    intake,
    intakeId,
    markNewPatientPayload,
    patientMatches,
    updateIntakeRecord,
  ]);

  const handleConfirmNewPatient = useCallback(async () => {
    const pushToKinnser = (intake as { push_to_kinnser?: boolean | null })
      .push_to_kinnser;

    await updateIntakeRecord(intakeId, {
      ...markNewPatientPayload,
      ...(pushToKinnser ? { kinnser_create_episode: true } : {}),
    });
  }, [intake, intakeId, markNewPatientPayload, updateIntakeRecord]);

  // =========================================================================
  // Field handlers
  // =========================================================================

  const handleFieldChange = useCallback(
    (fieldId: string, value: unknown, fieldType?: string) => {
      dirtyFieldsRef.current.add(fieldId);
      if (serverFieldErrors[fieldId]) {
        onServerFieldErrorCleared?.(fieldId);
      }
      setFormData(prev => ({ ...prev, [fieldId]: value }));

      if (fieldId === 'has_transferred_from_another_hha' && value !== true) {
        dirtyFieldsRef.current.add('prior_hha');
        setFormData(prev => ({ ...prev, prior_hha: null }));
        void saveField('prior_hha', null);
      }

      if (fieldType === 'boolean' || fieldType === 'dropdown') {
        void saveField(fieldId, value);
        return;
      }

      if (fieldType === 'text' || fieldType === 'list') {
        debouncedSave(fieldId, value);
      }
    },
    [
      setFormData,
      saveField,
      debouncedSave,
      serverFieldErrors,
      onServerFieldErrorCleared,
    ]
  );

  const handleFieldBlur = useCallback(
    (field: IntakeFieldDefinition, newValue?: unknown) => {
      cancelDebounce(field.field_id);

      const value =
        newValue !== undefined
          ? newValue
          : formData[field.field_id as keyof IntakeResponse];

      if (field.validator_fn) {
        const error = runValidation(field.validator_fn, value as string);
        setValidationErrors(prev => ({ ...prev, [field.field_id]: error }));
      }

      if (field.type === 'list') {
        const stringValue =
          typeof value === 'string'
            ? value
            : (value as string[] | undefined)?.join(', ') || '';
        const arrayValue = stringValue
          .split(',')
          .map(item => item.trim())
          .filter(item => item.length > 0);

        setFormData(prev => ({ ...prev, [field.field_id]: arrayValue }));

        const originalValue = intake[field.field_id as keyof IntakeResponse] as
          | string[]
          | undefined;
        const originalArray = originalValue || [];
        if (JSON.stringify(arrayValue) !== JSON.stringify(originalArray)) {
          void saveField(field.field_id, arrayValue);
        }
        return;
      }

      if (
        field.type === 'date' ||
        field.type === 'boolean' ||
        field.type === 'dropdown'
      ) {
        if (value !== intake[field.field_id as keyof IntakeResponse]) {
          void saveField(field.field_id, value);
        }
        return;
      }

      if (value !== intake[field.field_id as keyof IntakeResponse]) {
        void saveField(field.field_id, value);
      }
    },
    [formData, intake, saveField, cancelDebounce, setFormData]
  );

  // =========================================================================
  // Render
  // =========================================================================

  // Handle schema loading state
  if (schemaLoading) {
    return (
      <Card className='h-full flex flex-col'>
        <CardContent className='flex-1 flex items-center justify-center'>
          <div className='flex items-center gap-2 text-gray-500'>
            <Loader2 className='h-5 w-5 animate-spin' />
            <span>Loading form...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Handle schema error state
  if (schemaError || !schema) {
    return (
      <Card className='h-full flex flex-col'>
        <CardContent className='flex-1 flex items-center justify-center'>
          <div className='flex items-center gap-2 text-red-600'>
            <AlertCircle className='h-5 w-5' />
            <span>Failed to load form schema</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className='h-full flex flex-col'>
      <CardHeader className='border-b pb-3 bg-white'>
        <div className='flex items-center justify-between gap-3 w-full'>
          <div className='flex items-center gap-3'>
            {/* Save Status Indicator */}
            {saveStatus === 'saving' && (
              <div className='flex items-center gap-2 text-xs text-gray-500'>
                <Loader2 className='h-3 w-3 animate-spin' />
                <span>Saving...</span>
              </div>
            )}
            {saveStatus === 'saved' && (
              <div className='flex items-center gap-2 text-xs text-green-600'>
                <Check className='h-3 w-3' />
                <span>Saved</span>
              </div>
            )}
            {saveStatus === 'error' && (
              <div className='flex items-center gap-2 text-xs text-red-600'>
                <AlertCircle className='h-3 w-3' />
                <span>Save failed</span>
              </div>
            )}
          </div>
          <div className='flex items-center gap-3'>
            {/* Action Button - Mark Completed */}
            {visibleTabCount > 1 && (
              <MarkCompletedButton
                intakeId={intakeId}
                tabKey='extraction'
                isComplete={
                  intake.tab_statuses?.extraction === 'complete' ||
                  intake.tab_statuses?.extraction === 'complete_with_errors'
                }
                onStatusChange={onConfirm}
                className='h-7 px-3'
              />
            )}
          </div>
        </div>
        {/* Validation Error Summary */}
        {!formData.agency_clinic_id && agencyClinicOptions.length > 1 && (
          <Alert className='mt-3 py-2 px-3 bg-red-50 border-red-100 text-red-900 text-xs'>
            <div className='flex items-center gap-2'>
              <AlertCircle className='h-4 w-4 flex-shrink-0 text-red-600' />
              <span className='font-medium'>
                Clinic selection is required to proceed.
              </span>
            </div>
          </Alert>
        )}

        {/* Missing Fields Warning (Non-blocking) */}
        {missingFieldsCount > 0 && (
          <Alert className='mt-3 py-2 px-3 bg-amber-50 border-amber-100 text-amber-900 text-xs'>
            <div className='flex items-start gap-2'>
              <AlertCircle className='h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600' />
              <div className='flex-1'>
                <p className='font-medium mb-1'>
                  Invalid fields required to push to EMR:
                </p>
                <div className='flex flex-wrap gap-1'>
                  {missingFields.map((field, i) => (
                    <span key={field.field_id}>
                      {field.label}
                      {i < missingFields.length - 1 ? ',' : ''}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Alert>
        )}

        {/* Patient duplicate check (extraction tab) — Kinnser clinic_key or Roger EMR agency_clinic_id */}
        {duplicateCheckReady && (
          <div className='mt-3'>
            <DuplicatePatientBanner
              intake={intake}
              patientMatches={patientMatches}
              isMatchingPatients={isMatchingPatients}
              allowSelectExistingPatient={allowSelectExistingPatient}
              onOpenMatchModal={() => setPatientMatchModalOpen(true)}
              onConfirmNewPatient={handleConfirmNewPatient}
            />
          </div>
        )}
      </CardHeader>
      <CardContent className='flex-1 overflow-y-auto p-4'>
        {/* Clinic Selection - Only show if multiple clinics available */}
        {agencyClinicOptions.length > 1 && (
          <div className='mb-4'>
            <VirtualizedFormSelect
              ref={clinicSelectRef}
              label='Clinic'
              placeholder={
                agencyClinicsLoading
                  ? 'Loading clinics...'
                  : agencyClinicsError
                    ? 'Error loading clinics'
                    : 'Select clinic for this patient'
              }
              options={agencyClinicOptions}
              required={true}
              disabled={agencyClinicsLoading}
              error={
                !formData.agency_clinic_id ? 'Clinic is required' : undefined
              }
              name='agency_clinic_id'
              onChange={e => {
                const newValue = e.target.value;
                setFormData(prev => ({
                  ...prev,
                  agency_clinic_id: newValue,
                }));
                // Auto-save immediately when clinic changes
                if (newValue !== intake.agency_clinic_id) {
                  saveField('agency_clinic_id', newValue);
                }
              }}
              onBlur={() => {
                // Create a mock field definition for the blur handler
                const clinicField: IntakeFieldDefinition = {
                  field_id: 'agency_clinic_id',
                  label: 'Clinic',
                  type: 'dropdown',
                  required: true,
                  dropdown_source: 'agency_clinic',
                };
                handleFieldBlur(clinicField);
              }}
            />
          </div>
        )}

        <div className='space-y-5'>
          {sortedSections.map(([sectionKey, section]) => (
            <IntakeSectionRenderer
              key={sectionKey}
              sectionKey={sectionKey}
              section={section}
              formData={formData}
              validationErrors={displayedValidationErrors}
              onFieldChange={handleFieldChange}
              onFieldBlur={handleFieldBlur}
              showAIExplanations={showAIExplanations}
              agencyInsuranceOptions={agencyInsuranceOptions}
              agencyInsurancesLoading={agencyInsurancesLoading}
              agencyInsurancesError={agencyInsurancesError}
              agencyClinicOptions={agencyClinicOptions}
              agencyClinicsLoading={agencyClinicsLoading}
              agencyClinicsError={agencyClinicsError}
              selectedIntake={intake}
              documentNameDict={documentNameDict}
              dirtyFields={dirtyFieldsRef.current}
            />
          ))}
        </div>
      </CardContent>

      <PatientMatchModal
        patientMatchModalOpen={patientMatchModalOpen}
        setPatientMatchModalOpen={setPatientMatchModalOpen}
        patientMatches={patientMatches}
        allowSelectExisting={allowSelectExistingPatient}
        // Preselect: Roger matches persist as `patient_id`, Kinnser as `kinnser_matched_patient_key` (both match `PatientMatchResult.patient_key`).
        preselectedPatientKey={
          intake.patient_id ?? intake.kinnser_matched_patient_key
        }
        onSelectPatient={() => setPatientMatchModalOpen(false)}
        onDontMatch={handleConfirmNewPatient}
        onMatch={async patient => {
          const latest = getLatestActiveEpisode(patient.episodes_info ?? []);
          // The match identity lives in exactly one field, chosen by source:
          //   - Roger matches   -> `intake.patient_id` (Roger patient UUID)
          //   - Kinnser matches -> `intake.kinnser_matched_patient_key` (opaque Kinnser key)
          // We explicitly null the other so stale identifiers never linger
          // when the match is changed.
          const isRogerMatch = patient.match_source === 'roger_emr';
          const payload: Partial<IntakeResponse> = {
            is_new_patient: false,
            patient_id: isRogerMatch ? patient.patient_key : null,
            kinnser_matched_patient_key: isRogerMatch
              ? null
              : patient.patient_key,
            patient_match_source: patient.match_source,
          };
          if (latest) {
            const discharge = toEpisodeDateOnly(latest.discharge_date);
            const startOfCare = toEpisodeDateOnly(latest.start_of_care);
            const episodeStart = toEpisodeDateOnly(latest.start_date);
            const episodeEnd = toEpisodeDateOnly(latest.end_date);
            const episodeKey =
              latest.episode_key && latest.episode_key.trim() !== ''
                ? latest.episode_key.trim()
                : null;
            // Always persist all episode-derived fields (value or null) so the backend stays in sync when the match changes.
            payload.kinnser_matched_discharge_date = discharge ?? null;
            payload.kinnser_matched_start_of_care = startOfCare ?? null;
            payload.kinnser_matched_episode_start_date = episodeStart ?? null;
            payload.kinnser_matched_episode_end_date = episodeEnd ?? null;
            payload.kinnser_matched_episode_key = episodeKey;
            // Pre-populate clinician from matched episode's assigned clinician
            const clinicianKey = latest.am_user_key?.trim() || null;
            payload.kinnser_clinician_id = clinicianKey;
          } else {
            payload.kinnser_matched_discharge_date = null;
            payload.kinnser_matched_start_of_care = null;
            payload.kinnser_matched_episode_start_date = null;
            payload.kinnser_matched_episode_end_date = null;
            payload.kinnser_matched_episode_key = null;
            payload.kinnser_clinician_id = null;
          }
          await updateIntakeRecord(intakeId, payload);
        }}
      />
    </Card>
  );
};
